import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import {
  createBanner,
  updateBanner,
  deleteBanner,
  fetchBanners,
} from '../graphql/api/bannerApi';
import { Banner, BannerInput } from '../interfaces/product';
import './BannerManager.css';
import AlertMessage from '../components/AlertMessage';
import Accordion from '../components/Accordion';

const BannerManager: React.FC = () => {
  const defaultBannerInput: BannerInput = {
    header: '',
    midline: '',
    feature: '',
    discount: '',
    styles: {
      header: {
        color: '#ffffff',
        font: 'Arial',
        align: 'left',
        fontSize: '24px',
        fontWeight: '400',
        textShadow: 'none',
        letterSpacing: 'normal',
        lineHeight: 'normal',
      },
      midline: {
        color: '#ffffff',
        font: 'Arial',
        align: 'left',
        fontSize: '18px',
        fontWeight: '400',
        textShadow: 'none',
        letterSpacing: 'normal',
        lineHeight: 'normal',
      },
      feature: {
        color: '#ffffff',
        font: 'Arial',
        align: 'left',
        fontSize: '16px',
        fontWeight: '400',
        textShadow: 'none',
        letterSpacing: 'normal',
        lineHeight: 'normal',
      },
      discount: {
        color: '#ffeb3b',
        font: 'Arial',
        align: 'left',
        fontSize: '20px',
        fontWeight: '400',
        textShadow: 'none',
        letterSpacing: 'normal',
        lineHeight: 'normal',
      },
    },
    imageFile: undefined,
  };

  const [formData, setFormData] = useState<BannerInput>(defaultBannerInput);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [editId, setEditId] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  // Only these fields are text inputs
  type BannerTextKey = 'header' | 'midline' | 'feature' | 'discount';
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    fetchBanners()
      .then(data => {
        setBanners(data);
        setAlertMessage('Banners fetched successfully');
        setAlertType('success');
      })
      .catch(error => {
        console.error(error);
        setAlertMessage('Failed to fetch banners');
        setAlertType('error');
      });
  }, []);

    const resetForm = () => {
      setFormData(defaultBannerInput);
      setPreviewImage('');
      setEditId(null);

      // ✅ Clear file input manually
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };


  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('image uploaded')
      setAlertMessage('Image uploaded successfully');
    setAlertType('success');
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => setPreviewImage(reader.result as string);
    reader.readAsDataURL(file);

    setFormData(prev => ({ ...prev, imageFile: file }));
  
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    const key = name as BannerTextKey;
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleStyleChange = (
    section: BannerTextKey,
    field: keyof BannerInput['styles'][BannerTextKey],
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      styles: {
        ...prev.styles,
        [section]: {
          ...prev.styles[section],
          [field]: value,
        },
      },
    }));
  };

 const handleSubmit = async () => {
  try {
    if (editId != null) {
      await updateBanner(editId, formData);
    } else {
      await createBanner(formData);
    }

    // ✅ Re-fetch banners from the server
    const updatedList = await fetchBanners();
    setBanners(updatedList);

    setAlertMessage('Banner saved successfully');
    setAlertType('success');

    // ✅ Reset form state
    // setEditId(null);
    // setFormData(defaultBannerInput);
    // setPreviewImage('');
    resetForm();


  } catch (err) {
    console.error(err);
     resetForm();
    setAlertMessage('An error occurred while saving the banner');
    setAlertType('error');
   
  }
};


  const handleEdit = (b: Banner) => {
    setEditId(b.id || null);
    setPreviewImage(b.imageUrl);
    setFormData({
      header: b.header,
      midline: b.midline,
      feature: b.feature,
      discount: b.discount,
      styles: b.styles,
      imageFile: undefined,
    });
  };

  const handleDelete = async (id: string) => {
    await deleteBanner(id);
    setBanners(bs => bs.filter(b => b.id !== id));
    setAlertMessage('Banner deleted successfully');
    setAlertType('success');
  };



  const renderTextControls = (
    label: string,
    textKey: BannerTextKey,
    styleKey: BannerTextKey
  ) => (
    <div className="banner_control-group">
      {/* Group Label */}
      <label className="banner_group-label">{label}</label>
   
      {/* Text Input */}
      <div className="banner_text">
        <label>Text</label>
        <input
          type="text"
          name={textKey}
          placeholder={label}
          value={formData[textKey]}
          onChange={handleChange}
        />
      </div>

      {/* Style Options Grid */}
      <div className="banner_control-options">
        {/* Color */}
        <div className="banner_control-item">
          <label>Color</label>
          <input
            type="color"
            value={formData.styles[styleKey].color}
            onChange={e => handleStyleChange(styleKey, 'color', e.target.value)}
          />
        </div>

        {/* Font */}
        <div className="banner_control-item">
          <label>Font</label>
          <select
            value={formData.styles[styleKey].font}
            onChange={e => handleStyleChange(styleKey, 'font', e.target.value)}
          >
            <option value="Arial">Arial</option>
            <option value="Courier New">Courier New</option>
            <option value="Georgia">Georgia</option>
            <option value="Verdana">Verdana</option>
          </select>
        </div>

        {/* Align */}
        <div className="banner_control-item">
          <label>Align</label>
          <select
            value={formData.styles[styleKey].align}
            onChange={e => handleStyleChange(styleKey, 'align', e.target.value)}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>

        {/* Font Size */}
        <div className="banner_control-item">
          <label>Size</label>
          <select
            value={formData.styles[styleKey].fontSize}
            onChange={e => handleStyleChange(styleKey, 'fontSize', e.target.value)}
          >
            {Array.from({ length: 30 }, (_, i) => {
              const size = `${10 + i * 2}px`;
              return (
                <option key={size} value={size}>
                  {size}
                </option>
              );
            })}
          </select>
        </div>

        {/* Weight */}
        <div className="banner_control-item">
          <label>Weight</label>
          <select
            value={formData.styles[styleKey].fontWeight}
            onChange={e => handleStyleChange(styleKey, 'fontWeight', e.target.value)}
          >
            <option value="300">Light</option>
            <option value="400">Normal</option>
            <option value="700">Bold</option>
          </select>
        </div>

        {/* Shadow */}
        <div className="banner_control-item">
          <label>Shadow</label>
          <select
            value={formData.styles[styleKey].textShadow}
            onChange={e => handleStyleChange(styleKey, 'textShadow', e.target.value)}
          >
            <option value="none">None</option>
            <option value="1px 1px #000">Small Shadow</option>
            <option value="2px 2px #000">Medium Shadow</option>
            <option value="2px 2px #888">Soft Shadow</option>
          </select>
        </div>

        {/* Spacing */}
        <div className="banner_control-item">
          <label>Spacing</label>
          <select
            value={formData.styles[styleKey].letterSpacing}
            onChange={e => handleStyleChange(styleKey, 'letterSpacing', e.target.value)}
          >
            <option value="normal">Normal</option>
            <option value="1px">1px</option>
            <option value="2px">2px</option>
            <option value="3px">3px</option>
          </select>
        </div>

        {/* Line Height */}
        <div className="banner_control-item">
          <label>Line Height</label>
          <select
            value={formData.styles[styleKey].lineHeight}
            onChange={e => handleStyleChange(styleKey, 'lineHeight', e.target.value)}
          >
            <option value="normal">Normal</option>
            <option value="1">1</option>
            <option value="1.5">1.5</option>
            <option value="2">2</option>
          </select>
        </div>
      </div>
    </div>
  );


  return (
    <div className="banner_container">
      <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
      <div
        className="banner_main-content"
        style={{ marginLeft: isSidebarOpen ? '250px' : '60px' }}
      >
        <br />
        <br />
        <br />
        <div className="banner_top-section">
         <div className="banner_form-section">
            <h2>Banner / Slider Management</h2>
            <div className="banner_upload-container">
              <label>Upload Banner Image</label>
              <input  ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} />
            </div>

            <Accordion title="Line 1">
              {renderTextControls("Header", "header", "header")}
            </Accordion>

            <Accordion title="Line 2">
              {renderTextControls("Midline", "midline", "midline")}
            </Accordion>

            <Accordion title="Line 3">
              {renderTextControls("Feature", "feature", "feature")}
            </Accordion>

            <Accordion title="Line 4">
              {renderTextControls("Discount", "discount", "discount")}
            </Accordion>

            <button onClick={handleSubmit}>
              {editId != null ? "Update" : "Add"}
            </button>
          </div>

          <div className="banner_preview-section">
            <h3>Banner Preview</h3>
            <div
              className="banner_preview-container"
              style={{ backgroundImage: `url(${previewImage})` }}
            >
               {alertMessage && <AlertMessage message={alertMessage} type={alertType} />}

              <div className="banner_preview-overlay">
                {(['header', 'midline', 'feature', 'discount'] as const).map(key => (
                  <p
                    key={key}
                    style={{
                      color: formData.styles[key].color,
                      fontFamily: formData.styles[key].font,
                      textAlign: formData.styles[key].align as any,
                      fontSize: formData.styles[key].fontSize,
                      fontWeight: formData.styles[key].fontWeight,
                      textShadow: formData.styles[key].textShadow,
                      letterSpacing: formData.styles[key].letterSpacing,
                      lineHeight: formData.styles[key].lineHeight,
                    }}
                  >
                    {formData[key]}
                  </p>
                ))}
              </div>
            </div>
            <div className="banner_table-section">
              <h3>List of Banners</h3>
              <table>
                <thead>
                  <tr>
                    <th>Line 1</th>
                    <th>Line 2</th>
                    <th>Line 3</th>
                    <th>Line 4</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {banners.map(b => (
                    <tr key={b.id}>
                      <td>{b.header}</td>
                      <td>{b.midline}</td>
                      <td>{b.feature}</td>
                      <td>{b.discount}</td>
                      <td>
                        <button onClick={() => handleEdit(b)}>Edit</button>
                        <button onClick={() => handleDelete(b.id!)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerManager;
